import React, { useEffect, useCallback } from "react";

import FullPageLoader from "../FullPageLoader";

import * as loginApi from "js/api/loginApi";
import * as authApi from "js/api/authApi";
import { LoginDelegatedRequest } from "js/api/authApi";
import { useUserSessionStore } from "stores/v2/userSessionStore";
import { useLocation, useNavigate } from "react-router-dom";
import { datadogRum } from "@datadog/browser-rum";
const LoginMicrosoftCallback: React.FC = () => {
	const location = useLocation();
	const navigate = useNavigate();
	const handleLoginFailure = useCallback(
		(error: unknown): void => {
			const apiError = error as authApi.ApiError<authApi.LoginMethodError> | authApi.ApiError<authApi.ServerError>;
			useUserSessionStore.getState().handleAuthenticationFailure(apiError);

			// Extract applianceId from state query parameter if it exists
			// "state" value will only exist if initiated from School specific login page
			const searchParams = new URLSearchParams(location.search);
			const applianceId = searchParams.get("state") || undefined;

			// Track as user-facing error in RUM
			// Extract error message from respJson if available (ServerError case)
			// apiError.error is always undefined because FetchStatusError doesn't have an error property
			const errorMessage =
				apiError.respJson && "error" in apiError.respJson
					? apiError.respJson.error
					: error instanceof Error
						? error.message
						: "login failed";

			datadogRum.addError({
				message: "Azure AD OAuth login failed",
				source: "custom",
				stack: error instanceof Error ? error.stack : undefined,
				context: {
					provider: "Microsoft",
					errorType: errorMessage,
					errorMessage: errorMessage,
					...(applianceId ? { applianceId } : {}),
					...(apiError.respJson && "provider" in apiError.respJson ? { providerFromError: apiError.respJson.provider } : {}),
				},
			});

			if (apiError.respJson && "provider" in apiError.respJson) {
				navigate("/login", { state: { provider: apiError.respJson.provider } });
			} else {
				navigate("/login", { state: { error: "Microsoft" } });
			}
		},
		[navigate, location]
	);

	useEffect(() => {
		const searchParams = new URLSearchParams(location.search);
		const loginRequest: LoginDelegatedRequest = {
			code: searchParams.get("code") || "",
			deviceid: searchParams.get("state") || "",
		};

		authApi
			.loginDelegated(loginApi.LoginProvider.MICROSOFT, loginRequest)
			.then((res) => {
				useUserSessionStore
					.getState()
					.handleAuthenticationSuccess(res)
					.then(() => navigate("/"))
					.catch(handleLoginFailure);
			})
			.catch(handleLoginFailure);
	}, [navigate, handleLoginFailure, location]);

	return <FullPageLoader message="Logging in with Microsoft" />;
};

export default LoginMicrosoftCallback;
